import Accordion from '@/common/components/Accordion'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { FeeTextField } from '@/common/components/FeeTextField'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import useModalMessagesSimple from '@/common/hooks/useModalMessagesSimple'
import { Mapper } from '@/common/services/mapper'
import { unFormatCurrency, unFormatPercentage } from '@/common/utils/unFormatters'
import { useFeeTemplates } from '@/features/feeTemplates/hooks/useFeeTemplates'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import { useCreatePublicCompanyLeadContractFee } from '@/features/publicCompanyLeadsFunnel/hooks/useCreatePublicCompanyLeadContractFee'
import { useDeletePublicCompanyLeadContractFee } from '@/features/publicCompanyLeadsFunnel/hooks/useDeletePublicCompanyLeadContractFee'
import { useUpdatePublicCompanyLeadContractFee } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadContractFee'
import { PublicCompanyLeadsFunnelUpdateContractValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { ContractData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { ChangeEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface ContractFeesProps {
  contract?: ContractData
  disabled?: boolean
}

export const ContractFees = ({ contract, disabled }: ContractFeesProps) => {
  const { publicCompanyLeadId } = useParams()
  const queryClient = useQueryClient()

  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateContractValidationSchema>()

  const publicCompanyIsFetching = useIsFetching({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })

  const [searchFeeTemplate, setSearchFeeTemplate] = useDebounce('')

  const { data: feeTemplateOptions } = useFeeTemplates({
    filters: { search: searchFeeTemplate, category: 'public_company' },
    mapper: Mapper.mapToOptions<FeeTemplateData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
  }

  const { mutate: createPublicCompanyLeadContractFee, isPending: createPublicCompanyLeadContractFeeIsPending } =
    useCreatePublicCompanyLeadContractFee({
      publicCompanyLeadId,
      onSuccess: () => {
        setValue('contract.contract_fees', null)
        onSuccess()
      },
      onError: (error) => {
        setValue('contract.contract_fees', null)

        setMessages(error, { errorResponse: true });
      },
    })

  const {
    mutate: updatePublicCompanyLeadContractFee,
    isPending: updateContractFeeIsPending,
  } = useUpdatePublicCompanyLeadContractFee({
    publicCompanyLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
    },
    onError: (error) => {
      setMessages(error, { errorResponse: true });
    },
  })

  const {
    mutate: deletePublicCompanyLeadContactFee,
    isPending: deletePublicCompanyLeadContactFeeIsPending
  } = useDeletePublicCompanyLeadContractFee({
    publicCompanyLeadId,
    onSuccess,
    onError: (error) => {
      setMessages(error, { errorResponse: true });
    },
  })

  const submit = ({
    id,
    value,
    calculate_as,
    main_fee,
  }: {
    id: string
    value: string
    calculate_as: string
    main_fee?: boolean
  }) => {
    if (!/R\$|%/.test(value)) {
      return
    }

    const unFormatFunction = calculate_as === 'percentage' ? unFormatPercentage : unFormatCurrency
    value = `${unFormatFunction(value)}`

    updatePublicCompanyLeadContractFee({ id, value, main_fee })
  }

  const submitMainFee = ({ id, main_fee }: { id: string; main_fee: boolean }) => {
    updatePublicCompanyLeadContractFee({ id, main_fee })
  }

  return (
    <Accordion
      name="fee_templates"
      className="py-1"
      triggerDisabled={updateContractFeeIsPending}
      head={
        <p>
          Tarifas
        </p>
      }
      body={
        <div className="flex flex-col gap-4">
          <SelectController
            control={control}
            name="contract.contract_fees"
            placeholder="Pesquisar Tarifa"
            value={null}
            onInputChange={setSearchFeeTemplate}
            triggerOnChange={(newValue: { label: string; value: string }) => {
              createPublicCompanyLeadContractFee(newValue)
            }}
            options={
              feeTemplateOptions?.filter((feeTemplate) => {
                return !contract?.contract_fees?.some(({ fee_template }) => fee_template.id === feeTemplate.value)
              }) || []
            }
            isDisabled={
              !!publicCompanyIsFetching ||
              createPublicCompanyLeadContractFeeIsPending ||
              deletePublicCompanyLeadContactFeeIsPending ||
              disabled
            }
            isLoading={!!publicCompanyIsFetching || createPublicCompanyLeadContractFeeIsPending}
          />
          {contract?.contract_fees?.map((contractFee, index) => (
            <div key={contractFee.fee_template.id} className="flex gap-2">
              <Button
                type="button"
                variant="outline-error"
                className="size-[3.25rem] mt-5"
                onClick={() => deletePublicCompanyLeadContactFee(contractFee.id)}
                disabled={
                  !!publicCompanyIsFetching ||
                  updateContractFeeIsPending ||
                  deletePublicCompanyLeadContactFeeIsPending ||
                  disabled
                }
              >
                <XIcon />
              </Button>

              <div className="w-1/2 flex flex-col gap-y-2">
                <TextField label="Categoria" defaultValue={contractFee.fee_template.display_name} disabled />

                {contractFee.fee_template.calculate_as === 'percentage' && (
                  <Checkbox
                    label="Tarifa principal"
                    className="gap-2"
                    labelClassName="text-[10px] font-light italic"
                    dataTestidPrefix='contract_fees_main_fee'
                    defaultChecked={watch(`contract.contract_fees.${index}.main_fee`) || contractFee.main_fee}
                    {...register(`contract.contract_fees.${index}.main_fee`)}
                    errorMessage={errors?.contract?.contract_fees?.[index]?.main_fee?.message || undefined}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      register(`contract.contract_fees.${index}.main_fee`).onChange(event)
                      submitMainFee({
                        id: contractFee.id,
                        main_fee: event.target.checked,
                      })
                    }}
                    disabled={
                      !!publicCompanyIsFetching ||
                      updateContractFeeIsPending ||
                      deletePublicCompanyLeadContactFeeIsPending ||
                      disabled
                    }
                  />
                )}
              </div>

              {contractFee.fee_template && (
                <FeeTextField
                  contractFee={contractFee}
                  fieldName={`contract.contract_fees.${index}.value`}
                  submit={submit}
                  disabled={
                    !!publicCompanyIsFetching ||
                    updateContractFeeIsPending ||
                    deletePublicCompanyLeadContactFeeIsPending ||
                    disabled
                  }
                  errorMessage={errors?.contract?.contract_fees?.[index]?.value?.message}
                  className="w-1/2"
                />
              )}
            </div>
          ))}
        </div>
      }
    />
  )
}
